/**
 * ArrayRipetibileWidget — RD4: sotto-campi driven da config.
 *
 * Legge `sotto_campi` da `sezione.campo.config.sotto_campi`. Ogni riga
 * renderizza dinamicamente i sotto-campi in base al `tipo`:
 * text, textarea, enum, number, bool, date.
 *
 * Se la config non è disponibile, fallback al preset interventi_sospesi.
 */
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Trash2 } from "lucide-react";

const FALLBACK_INTERVENTI = {
  sotto_campi: [
    { key: "descrizione", label: "Descrizione", tipo: "text", ordine: 1, required: true, col_span: 2 },
    { key: "categoria", label: "Categoria", tipo: "text", ordine: 2 },
    { key: "priorita", label: "Priorità", tipo: "enum", ordine: 3, enum_options: [
      { value: "bassa", label: "Bassa" }, { value: "media", label: "Media" },
      { value: "alta", label: "Alta" }, { value: "urgente", label: "Urgente" },
    ] },
    { key: "responsabile_interno", label: "Responsabile interno", tipo: "text", ordine: 4 },
    { key: "fornitore", label: "Fornitore", tipo: "text", ordine: 5 },
    { key: "stato", label: "Stato", tipo: "enum", ordine: 6, enum_options: [
      { value: "aperto", label: "Aperto" }, { value: "in_corso", label: "In corso" },
      { value: "in_attesa_fornitore", label: "In attesa fornitore" }, { value: "chiuso", label: "Chiuso" },
    ] },
    { key: "data_prevista_chiusura", label: "Data prevista chiusura", tipo: "date", ordine: 7 },
    { key: "note", label: "Note", tipo: "textarea", ordine: 8, col_span: 2 },
  ],
  min_rows: 0, max_rows: null,
};

function defaultForType(t) {
  if (t === "bool") return false;
  if (t === "number") return null;
  return "";
}

function newRow(sotto_campi) {
  const r = { id: Math.random().toString(36).slice(2, 8) };
  sotto_campi.forEach((sc) => { r[sc.key] = defaultForType(sc.tipo); });
  return r;
}

export function ArrayRipetibileWidget({ campoKey, label, value, onChange, isReadOnly, config }) {
  const cfg = config && config.sotto_campi ? config : FALLBACK_INTERVENTI;
  const sotto_campi = (cfg.sotto_campi || []).slice().sort((a, b) => (a.ordine || 0) - (b.ordine || 0));
  const rows = Array.isArray(value) ? value : [];
  const maxRows = cfg.max_rows;
  const minRows = cfg.min_rows || 0;

  const addRow = () => {
    if (maxRows && rows.length >= maxRows) return;
    onChange([...rows, newRow(sotto_campi)]);
  };
  const removeRow = (idx) => {
    if (rows.length <= minRows) return;
    onChange(rows.filter((_, i) => i !== idx));
  };
  const updateRow = (idx, k, v) => onChange(rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));

  const renderField = (sc, r, idx) => {
    const spanCls = sc.col_span === 2 ? "md:col-span-2" : "";
    const val = r[sc.key] ?? (sc.tipo === "bool" ? false : "");
    const testid = `array-field-${campoKey}-${idx}-${sc.key}`;
    if (sc.tipo === "textarea") {
      return (
        <Textarea key={sc.key} placeholder={sc.placeholder || sc.label}
          value={val || ""} onChange={(e) => updateRow(idx, sc.key, e.target.value)}
          className={`min-h-[40px] ${spanCls}`} disabled={isReadOnly} data-testid={testid} />
      );
    }
    if (sc.tipo === "enum") {
      return (
        <div key={sc.key} className={spanCls}>
          <Select value={val || ""} onValueChange={(v) => updateRow(idx, sc.key, v)} disabled={isReadOnly}>
            <SelectTrigger data-testid={testid}><SelectValue placeholder={sc.placeholder || sc.label} /></SelectTrigger>
            <SelectContent>
              {(sc.enum_options || []).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (sc.tipo === "date") {
      return (
        <Input key={sc.key} type="date" value={val || ""}
          onChange={(e) => updateRow(idx, sc.key, e.target.value)}
          className={spanCls} disabled={isReadOnly} data-testid={testid} />
      );
    }
    if (sc.tipo === "number") {
      return (
        <Input key={sc.key} type="number" step={sc.step != null ? String(sc.step) : "any"}
          placeholder={sc.placeholder || sc.label}
          value={val ?? ""} onChange={(e) => updateRow(idx, sc.key, e.target.value === "" ? null : parseFloat(e.target.value))}
          className={spanCls} disabled={isReadOnly} data-testid={testid} />
      );
    }
    if (sc.tipo === "bool") {
      return (
        <div key={sc.key} className={`flex items-center gap-2 ${spanCls}`} data-testid={testid}>
          <Switch checked={!!val} onCheckedChange={(v) => updateRow(idx, sc.key, v)} disabled={isReadOnly} />
          <span className="text-xs text-muted-foreground">{sc.label}</span>
        </div>
      );
    }
    // text (default)
    return (
      <Input key={sc.key} placeholder={sc.placeholder || sc.label}
        value={val || ""} onChange={(e) => updateRow(idx, sc.key, e.target.value)}
        className={spanCls} disabled={isReadOnly} data-testid={testid} />
    );
  };

  return (
    <div className="md:col-span-2 space-y-2" data-testid={`widget-array-${campoKey}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          {label} ({rows.length}
          {maxRows ? ` / max ${maxRows}` : ""}
          {minRows ? ` · min ${minRows}` : ""})
        </div>
        {!isReadOnly && (
          <Button size="sm" variant="outline" onClick={addRow} disabled={!!(maxRows && rows.length >= maxRows)} data-testid={`array-add-${campoKey}`}>
            <Plus className="h-3.5 w-3.5" /> Aggiungi
          </Button>
        )}
      </div>

      <div className="space-y-2">
        {rows.map((r, idx) => (
          <div key={r.id || idx} className="rounded-lg border border-border/60 p-3 space-y-2" data-testid={`array-row-${campoKey}-${idx}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
              {sotto_campi.map((sc) => renderField(sc, r, idx))}
            </div>
            {!isReadOnly && rows.length > minRows && (
              <Button size="sm" variant="ghost" onClick={() => removeRow(idx)} className="text-rose-500" data-testid={`array-remove-${campoKey}-${idx}`}>
                <Trash2 className="h-3.5 w-3.5" /> Rimuovi
              </Button>
            )}
          </div>
        ))}
        {rows.length === 0 && (
          <p className="text-xs text-muted-foreground text-center py-3">Nessun elemento. Aggiungi la prima riga.</p>
        )}
      </div>
    </div>
  );
}
